import React, { useEffect, useState } from "react";
import { useClientInfo } from "../../utils/commonImports";
import axiosInstance from "../../api/axiosInstance";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

/* =========================================================
   EMPTY FORM
========================================================= */
const emptyForm = {
  id: null,
  template_name: "",
  template_type: "INVOICE",
  language: "en",
  meta_template_id: "",
  status: "DRAFT",
  category: "UTILITY",
  components: [
    {
      type: "BODY",
      text: "",
    },
  ],
  is_active: true,
};

/* =========================================================
   COMPONENT
========================================================= */
export default function WhatsAppCloudTemplates() {
  const { clientId } = useClientInfo();

  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);

  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =========================================================
     SNACKBAR
  ========================================================= */
  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  /* =========================================================
     LOAD TEMPLATES
  ========================================================= */
  const loadTemplates = async () => {
    if (!clientId) return;

    try {
      setLoading(true);

      const response = await axiosInstance.get(
        `/api/whatsapp/cloud/templates/${clientId}`,
      );

      if (response.data?.success) {
        setTemplates(response.data.templates || []);
      } else {
        showMessage(
          response.data?.message || "Failed to load templates",
          "error",
        );
      }
    } catch (error) {
      console.error("WhatsApp Cloud Template Load Error:", error);

      showMessage(
        error.response?.data?.message || "Failed to load WhatsApp templates",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */
  useEffect(() => {
    loadTemplates();
  }, [clientId]);

  /* =========================================================
     INPUT CHANGE
  ========================================================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* =========================================================
     BODY TEXT CHANGE
  ========================================================= */
  const handleBodyChange = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      components: [
        {
          ...(prev.components?.[0] || {}),
          type: "BODY",
          text: value,
        },
        ...(prev.components?.slice(1) || []),
      ],
    }));
  };

  /* =========================================================
     HEADER TEXT CHANGE
  ========================================================= */
  const handleHeaderChange = (e) => {
    const value = e.target.value;

    setForm((prev) => {
      const components = [...(prev.components || [])];

      const headerIndex = components.findIndex(
        (item) => item.type === "HEADER",
      );

      if (headerIndex >= 0) {
        components[headerIndex] = {
          ...components[headerIndex],
          type: "HEADER",
          format: "TEXT",
          text: value,
        };
      } else if (value.trim()) {
        components.unshift({
          type: "HEADER",
          format: "TEXT",
          text: value,
        });
      }

      return {
        ...prev,
        components,
      };
    });
  };

  /* =========================================================
     FOOTER CHANGE
  ========================================================= */
  const handleFooterChange = (e) => {
    const value = e.target.value;

    setForm((prev) => {
      let components = [...(prev.components || [])];

      const footerIndex = components.findIndex(
        (item) => item.type === "FOOTER",
      );

      if (footerIndex >= 0) {
        if (value.trim()) {
          components[footerIndex] = {
            ...components[footerIndex],
            type: "FOOTER",
            text: value,
          };
        } else {
          components.splice(footerIndex, 1);
        }
      } else if (value.trim()) {
        components.push({
          type: "FOOTER",
          text: value,
        });
      }

      return {
        ...prev,
        components,
      };
    });
  };

  /* =========================================================
     GET BODY
  ========================================================= */
  const getBodyText = () => {
    const body = form.components?.find((item) => item.type === "BODY");

    return body?.text || "";
  };

  /* =========================================================
     GET HEADER
  ========================================================= */
  const getHeaderText = () => {
    const header = form.components?.find((item) => item.type === "HEADER");

    return header?.text || "";
  };

  /* =========================================================
     GET FOOTER
  ========================================================= */
  const getFooterText = () => {
    const footer = form.components?.find((item) => item.type === "FOOTER");

    return footer?.text || "";
  };

  /* =========================================================
     NEW TEMPLATE
  ========================================================= */
  const handleNew = () => {
    setForm({
      ...emptyForm,
      components: [
        {
          type: "BODY",
          text: "",
        },
      ],
    });

    setShowForm(true);
  };

  /* =========================================================
     EDIT TEMPLATE
  ========================================================= */
  const handleEdit = (template) => {
    let components = template.components || [];

    if (typeof components === "string") {
      try {
        components = JSON.parse(components);
      } catch {
        components = [];
      }
    }

    if (!Array.isArray(components)) {
      components = [];
    }

    setForm({
      id: template.id,
      template_name: template.template_name || "",
      template_type: template.template_type || "INVOICE",
      language: template.language || "en",
      meta_template_id: template.meta_template_id || "",
      status: template.status || "DRAFT",
      category: template.category || "UTILITY",
      components,
      is_active: template.is_active !== false,
    });

    setShowForm(true);
  };

  /* =========================================================
     SAVE TEMPLATE
  ========================================================= */
  const handleSave = async () => {
    if (!clientId) {
      showMessage("Client ID is missing", "error");
      return;
    }

    if (!form.template_name.trim()) {
      showMessage("Template name is required", "error");
      return;
    }

    const bodyText = getBodyText();

    if (!bodyText.trim()) {
      showMessage("Message body is required", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        client_id: clientId,
        components: form.components || [],
      };

      const response = await axiosInstance.post(
        "/api/whatsapp/cloud/templates/save",
        payload,
      );

      if (response.data?.success) {
        showMessage(
          response.data.message || "WhatsApp template saved successfully",
        );

        setShowForm(false);
        setForm(emptyForm);

        await loadTemplates();
      } else {
        showMessage(response.data?.message || "Save failed", "error");
      }
    } catch (error) {
      console.error("WhatsApp Cloud Template Save Error:", error);

      showMessage(
        error.response?.data?.message || "Failed to save WhatsApp template",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE TEMPLATE
  ========================================================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      const response = await axiosInstance.delete(
        `/api/whatsapp/cloud/templates/${id}`,
      );

      if (response.data?.success) {
        showMessage("WhatsApp template deleted successfully");

        await loadTemplates();
      } else {
        showMessage(response.data?.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("WhatsApp Cloud Template Delete Error:", error);

      showMessage(
        error.response?.data?.message || "Failed to delete template",
        "error",
      );
    }
  };

  /* =========================================================
     CANCEL
  ========================================================= */
  const handleCancel = () => {
    setShowForm(false);
    setForm(emptyForm);
  };

  return (
    <div className="p-6 bg-white rounded shadow-md max-w-6xl mx-auto">
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            WhatsApp Cloud API Templates
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Manage message templates used by WhatsApp Cloud API
          </p>
        </div>

        {!showForm && (
          <button
            onClick={handleNew}
            className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700"
          >
            + New Template
          </button>
        )}
      </div>

      {/* =====================================================
          FORM
      ===================================================== */}
      {showForm && (
        <div className="border rounded-lg p-5 mb-6 bg-gray-50">
          <h3 className="text-lg font-semibold mb-5">
            {form.id ? "Edit WhatsApp Template" : "New WhatsApp Template"}
          </h3>

          {/* =================================================
              BASIC DETAILS
          ================================================= */}
          <div className="grid grid-cols-2 gap-5">
            {/* TEMPLATE NAME */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Template Name
              </label>

              <input
                type="text"
                name="template_name"
                value={form.template_name}
                onChange={handleChange}
                placeholder="invoice_notification"
                className="border border-gray-300 rounded px-3 py-2 w-full outline-none focus:ring-2 focus:ring-blue-500"
              />

              <p className="text-xs text-gray-500 mt-1">
                Use lowercase letters, numbers and underscores.
              </p>
            </div>

            {/* TEMPLATE TYPE */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Template Type
              </label>

              <select
                name="template_type"
                value={form.template_type}
                onChange={handleChange}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              >
                <option value="INVOICE">Invoice</option>
                <option value="PAYMENT">Payment</option>
                <option value="BOOKING">Booking</option>
                <option value="REMINDER">Reminder</option>
                <option value="TRIPSHEET_ORDERBY">Tripsheet Orderby</option>
                <option value="TRIPSHEET_USER">Tripsheet User</option>
                <option value="TRIPSHEET_DRIVER">Tripsheet Driver</option>
                <option value="TRIPSHEET_VENDOR">Tripsheet Vendor</option>

                <option value="GENERAL">General</option>
              </select>
            </div>

            {/* LANGUAGE */}
            <div>
              <label className="block text-sm font-medium mb-1">Language</label>

              <select
                name="language"
                value={form.language}
                onChange={handleChange}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              >
                <option value="en">English</option>
                <option value="en_US">English (US)</option>
                <option value="hi">Hindi</option>
                <option value="kn">Kannada</option>
                <option value="ta">Tamil</option>
                <option value="te">Telugu</option>
                <option value="ml">Malayalam</option>
              </select>
            </div>

            {/* CATEGORY */}
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>

              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              >
                <option value="UTILITY">Utility</option>
                <option value="MARKETING">Marketing</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
            </div>
          </div>

          {/* =================================================
              HEADER
          ================================================= */}
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">
              Header Text
            </label>

            <input
              type="text"
              value={getHeaderText()}
              onChange={handleHeaderChange}
              placeholder="Optional header"
              className="border border-gray-300 rounded px-3 py-2 w-full"
            />
          </div>

          {/* =================================================
              BODY
          ================================================= */}
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">
              Message Body
            </label>

            <textarea
              value={getBodyText()}
              onChange={handleBodyChange}
              rows={8}
              placeholder={`Dear {{1}},

Your invoice {{2}} for {{3}} is ready.

Please contact us if you have any questions.

Thank you for your business.`}
              className="border border-gray-300 rounded px-3 py-2 w-full outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="text-xs text-gray-500 mt-2">
              Use variables like:
              <span className="font-semibold ml-1">
                {"{{1}}"}, {"{{2}}"}, {"{{3}}"}
              </span>
            </div>
          </div>

          {/* =================================================
              FOOTER
          ================================================= */}
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">Footer</label>

            <input
              type="text"
              value={getFooterText()}
              onChange={handleFooterChange}
              placeholder="Powered by WheelsERP Prime"
              className="border border-gray-300 rounded px-3 py-2 w-full"
            />
          </div>

          {/* =================================================
              META TEMPLATE ID
          ================================================= */}
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">
              Meta Template ID
            </label>

            <input
              type="text"
              name="meta_template_id"
              value={form.meta_template_id}
              onChange={handleChange}
              placeholder="Will be available after Meta registration"
              className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100"
            />

            <p className="text-xs text-gray-500 mt-1">
              Leave empty until the template is registered with Meta.
            </p>
          </div>

          {/* =================================================
              STATUS
          ================================================= */}
          <div className="mt-5">
            <label className="block text-sm font-medium mb-1">Status</label>

            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 w-full"
            >
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          {/* =================================================
              ACTIVE
          ================================================= */}
          <div className="flex items-center mt-5">
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={handleChange}
              className="mr-2"
            />

            <label className="text-sm">Active</label>
          </div>

          {/* =================================================
              BUTTONS
          ================================================= */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Template"}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving}
              className="bg-gray-300 px-6 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* =====================================================
          TEMPLATE LIST
      ===================================================== */}
      {!showForm && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left px-4 py-3">Template Name</th>

                <th className="text-left px-4 py-3">Type</th>

                <th className="text-left px-4 py-3">Language</th>

                <th className="text-left px-4 py-3">Category</th>

                <th className="text-left px-4 py-3">Status</th>

                <th className="text-left px-4 py-3">Active</th>

                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-500">
                    Loading WhatsApp templates...
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-500">
                    No WhatsApp templates found.
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      {template.template_name}
                    </td>

                    <td className="px-4 py-3">
                      {template.template_type || "-"}
                    </td>

                    <td className="px-4 py-3">{template.language || "en"}</td>

                    <td className="px-4 py-3">{template.category || "-"}</td>

                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded text-xs bg-gray-100">
                        {template.status || "PENDING"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      {template.is_active ? "Yes" : "No"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-blue-600 hover:underline mr-4"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* =====================================================
          SNACKBAR
      ===================================================== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MuiAlert
          elevation={6}
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
